/**
 * Shared constants, types, and utilities for Quotes tab popup cell editors.
 * Used by FieldPopoverEditor and TypedAmountCellEditor.
 */

import { paletteVar, paletteVarAlpha } from "@platform/design-system";

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

export const CENTRO_PURPLE = paletteVar("primary-main", "#832A80");

export const CUSTOM_TEXT_RADIO_SX = {
  color: CENTRO_PURPLE,
  "&.Mui-checked": {
    color: CENTRO_PURPLE,
  },
};

export const CUSTOM_TEXT_INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: paletteVarAlpha(CENTRO_PURPLE, 0.025),
    "& fieldset": {
      borderColor: paletteVarAlpha(CENTRO_PURPLE, 0.55),
    },
    "&:hover fieldset": {
      borderColor: paletteVarAlpha(CENTRO_PURPLE, 0.7),
    },
    "&.Mui-focused fieldset": {
      borderColor: CENTRO_PURPLE,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: CENTRO_PURPLE,
  },
};

/**
 * Same accent treatment as `CUSTOM_TEXT_INPUT_SX`, applied to a plain Box
 * wrapper rather than an `OutlinedInput` — for the rich-text custom-text
 * editor, which isn't a MUI input.
 *
 * `RichTextEditor` sizes itself via `flex: 1` / `minHeight: 0`, which only
 * resolves against a flex ancestor with a bounded height — without
 * `display: flex` + a fixed `height` here, long content grows the box
 * instead of scrolling inside it.
 */
export const CUSTOM_TEXT_EDITOR_SX = {
  display: "flex",
  flexDirection: "column",
  height: 140,
  overflow: "hidden",
  border: `1px solid ${paletteVarAlpha(CENTRO_PURPLE, 0.55)}`,
  borderRadius: 1,
  backgroundColor: paletteVarAlpha(CENTRO_PURPLE, 0.025),
  "&:hover": {
    borderColor: paletteVarAlpha(CENTRO_PURPLE, 0.7),
  },
  "&:focus-within": {
    borderColor: CENTRO_PURPLE,
  },
};

/**
 * Formats a dollar amount as a USD currency string with no decimal places
 * (e.g. 4000 → "$4,000").
 */
export const quotesUsdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

/** Whether the popup editor is in benefit-edit or custom-text mode. */
export type EditMode = "benefit" | "customText";

// ---------------------------------------------------------------------------
// Shared utilities
// ---------------------------------------------------------------------------

/**
 * Trims a custom text draft and returns null for blank input
 * (null signals "clear the override" to the persistence layer).
 */
export const parseCustomText = (draft: string): string | null => {
  const trimmed = draft.trim();
  return trimmed === "" ? null : trimmed;
};

/**
 * Same "clear the override" contract as `parseCustomText`, but for rich-text
 * HTML drafts — an empty rich-text draft still serialises to markup like
 * `<p></p>`, so blankness comes from the editor's own character count
 * rather than a string trim.
 */
export const parseRichCustomText = (
  html: string,
  characterCount: number,
): string | null => (characterCount === 0 ? null : html);

/**
 * Approximate initial character count for a saved rich-text custom-text
 * value — exact only matters once the editor itself reports a count via
 * `onCharacterCount` (see `parseRichCustomText`).
 */
export const initialCharacterCount = (
  text: string | null | undefined,
): number => text?.length ?? 0;

/**
 * Overflow guard: amounts above this can't round-trip through the backend's
 * integer column, so reject them client-side rather than 400 on save.
 */
export const MAX_AMOUNT = 10_000_000_000;

/**
 * Validate a numeric input against min / max (and whole-number, when
 * `integer` is set) rules, mirroring the backend so we block the save instead
 * of round-tripping a 400. Empty is rejected unless `optional` is set. Returns
 * an error message, or "" when acceptable.
 */
export const validateAmountInput = (
  raw: string,
  {
    min,
    max,
    integer,
    optional = false,
  }: { min: number; max?: number; integer: boolean; optional?: boolean },
): string => {
  if (raw === "") return optional ? "" : "Required";
  const value = Number(raw);
  if (!Number.isFinite(value)) return "Enter a number";
  if (integer && !Number.isInteger(value)) return "Enter a whole number";
  if (value < min) return `Must be at least ${min}`;
  if (max !== undefined && value > max) return `Must be at most ${max}`;
  if (value > MAX_AMOUNT) return "Value is too large";
  return "";
};
